import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, CheckCircle, Send, Loader2, AlertCircle } from 'lucide-react';
import { APP_CONFIG } from '../../config/app.config';
import { enquiryErrorMessage, sendEnquiryEmail } from '../../utils/sendEnquiryEmail';

const EMPTY_FORM = {
  firstName: '', lastName: '', phone: '', email: '',
  subject: 'General Inquiry', message: '',
};

const ContactPage: React.FC = () => {
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const update = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }));
  const resetForm = () => { setForm(EMPTY_FORM); setSent(false); setError(null); };

  // Same mail path as the quote wizard, so both forms reach the business alike.
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!form.firstName.trim() || !form.lastName.trim()) {
      setError('Please enter your first and last name.');
      return;
    }
    // The copy beside these fields promises one contact method is enough, so the
    // form must not insist on both — but it does need one to reply on.
    if (!form.phone.trim() && !form.email.trim()) {
      setError('Please provide either a phone number or an email address.');
      return;
    }
    if (!form.message.trim()) {
      setError('Please tell us how we can help.');
      return;
    }

    setError(null);
    setSending(true);

    try {
      await sendEnquiryEmail({
        fullName: `${form.firstName.trim()} ${form.lastName.trim()}`,
        phoneNumber: form.phone,
        emailAddress: form.email,
        serviceNeeded: [
          `${form.subject}`,
          form.message.trim(),
        ].join('\n'),
      });
      setSent(true);
    } catch (err) {
      setError(enquiryErrorMessage(
        err,
        'Something went wrong sending your message. Please try again or call us directly.'
      ));
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <div className="page-header" style={{ paddingTop: '7rem' }}>
        <h1 style={{ color: 'white', fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: 800, marginBottom: '1rem' }}>Contact Us</h1>
        <p style={{ color: '#e6edf5', maxWidth: '560px', margin: '0 auto', fontSize: '1rem' }}>
          Have a question? Ready to schedule? Call us, email us, or send the form below —
          whichever you prefer. You never have to pick up the phone to get an answer from us.
          Typical response time: under 2 hours.
        </p>
      </div>

      <div className="container-xl section-padding">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.5fr', gap: '4rem', alignItems: 'start' }}>
          {/* Info */}
          <div>
            <h2 style={{ fontWeight: 800, fontSize: '1.5rem', marginBottom: '0.5rem', color: 'var(--text-primary)' }}>Get in Touch</h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', lineHeight: 1.65, marginBottom: '1.5rem' }}>
              Prefer not to talk on the phone? Email us or use the form — a real person replies either way.
            </p>
            {[
              { icon: <Mail size={20} color="#1493e6" />, label: 'Email — no phone call needed', value: APP_CONFIG.email, href: `mailto:${APP_CONFIG.email}` },
              { icon: <Phone size={20} color="#1493e6" />, label: 'Phone', value: APP_CONFIG.phone, href: `tel:${APP_CONFIG.phoneRaw}` },
              { icon: <MapPin size={20} color="#1493e6" />, label: 'Office', value: `${APP_CONFIG.address}, ${APP_CONFIG.city}, ${APP_CONFIG.state}`, href: undefined },
            ].map(item => (
              <div key={item.label} style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', alignItems: 'flex-start' }}>
                <div style={{ width: '2.75rem', height: '2.75rem', borderRadius: '0.625rem', background: 'rgb(20 147 230 / 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {item.icon}
                </div>
                <div>
                  <p style={{ fontWeight: 600, color: 'var(--text-secondary)', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.25rem' }}>{item.label}</p>
                  {item.href ? (
                    <a href={item.href} style={{ fontWeight: 600, color: 'var(--text-primary)', textDecoration: 'none', fontSize: '1rem' }}>{item.value}</a>
                  ) : (
                    <p style={{ fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>{item.value}</p>
                  )}
                </div>
              </div>
            ))}

            <div className="card" style={{ background: 'var(--bg-secondary)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.875rem' }}>
                <Clock size={18} color="#1493e6" />
                <span style={{ fontWeight: 700, color: 'var(--text-primary)' }}>Business Hours</span>
              </div>
              {[
                { day: 'Monday – Friday', hours: APP_CONFIG.businessHours.weekdays },
                { day: 'Saturday', hours: APP_CONFIG.businessHours.saturday },
                { day: 'Sunday', hours: APP_CONFIG.businessHours.sunday },
              ].map(row => (
                <div key={row.day} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid var(--border-color)', fontSize: '0.875rem' }}>
                  <span style={{ color: 'var(--text-secondary)' }}>{row.day}</span>
                  <span style={{ fontWeight: 600, color: row.day === 'Sunday' ? '#1493e6' : 'var(--text-primary)' }}>{row.hours}</span>
                </div>
              ))}
              <p style={{ marginTop: '0.875rem', fontSize: '0.8rem', color: '#1493e6', fontWeight: 600 }}>🚨 24/7 Emergency Line Always Available</p>
            </div>
          </div>

          {/* Form */}
          <div className="card">
            {sent ? (
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <CheckCircle size={48} color="#1493e6" style={{ marginBottom: '1rem' }} />
                <h3 style={{ fontWeight: 700, fontSize: '1.25rem', marginBottom: '0.5rem', color: 'var(--text-primary)' }}>Message Sent!</h3>
                <p style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem' }}>
                  Thanks, <strong style={{ color: 'var(--text-primary)' }}>{form.firstName}</strong> — we'll get back to you within 2 hours during business hours.
                </p>
                <button className="btn-secondary" onClick={resetForm}>Send Another Message</button>
              </div>
            ) : (
              <>
                <h2 style={{ fontWeight: 700, fontSize: '1.25rem', marginBottom: '1.5rem', color: 'var(--text-primary)' }}>Send Us a Message</h2>
                {error && (
                  <div style={{
                    display: 'flex', alignItems: 'flex-start', gap: '0.6rem',
                    background: 'rgb(220 38 38 / 0.08)', border: '1px solid rgb(220 38 38 / 0.35)',
                    borderRadius: '0.5rem', padding: '0.75rem 1rem', marginBottom: '1.25rem',
                  }}>
                    <AlertCircle size={18} color="#dc2626" style={{ flexShrink: 0, marginTop: '0.1rem' }} />
                    <span style={{ fontSize: '0.85rem', color: '#dc2626' }}>{error}</span>
                  </div>
                )}
                <form onSubmit={handleSubmit}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label className="label">First Name *</label>
                    <input className="input-field" placeholder="John" value={form.firstName} onChange={e => update('firstName', e.target.value)} />
                  </div>
                  <div>
                    <label className="label">Last Name *</label>
                    <input className="input-field" placeholder="Smith" value={form.lastName} onChange={e => update('lastName', e.target.value)} />
                  </div>
                  <div>
                    <label className="label">Phone</label>
                    <input className="input-field" placeholder="(904) 555-0000" value={form.phone} onChange={e => update('phone', e.target.value)} />
                  </div>
                  <div>
                    <label className="label">Email</label>
                    <input className="input-field" type="email" placeholder="john@email.com" value={form.email} onChange={e => update('email', e.target.value)} />
                  </div>
                  <div style={{ gridColumn: '1 / -1', marginTop: '-0.35rem' }}>
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.82rem', margin: 0 }}>
                      Give us <strong style={{ color: 'var(--text-primary)' }}>either a phone number or an email address</strong> —
                      whichever you prefer to be contacted on. One is enough.
                    </p>
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label className="label">Subject</label>
                    <select className="input-field" value={form.subject} onChange={e => update('subject', e.target.value)}>
                      <option>General Inquiry</option>
                      <option>Request an Estimate</option>
                      <option>Schedule an Inspection</option>
                      <option>Insurance Claim Help</option>
                      <option>Emergency Service</option>
                      <option>Other</option>
                    </select>
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label className="label">Message *</label>
                    <textarea className="input-field" rows={5} placeholder="Tell us about your roofing needs..." value={form.message} onChange={e => update('message', e.target.value)} style={{ resize: 'vertical' }} />
                  </div>
                </div>
                <button type="submit" className="btn-primary" style={{ marginTop: '1.25rem', width: '100%', justifyContent: 'center' }} disabled={sending}>
                  {sending ? (<><Loader2 size={16} className="spin" /> Sending...</>) : (<><Send size={16} /> Send Message</>)}
                </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
      <style>{`
        @media(max-width:768px){div[style*="grid-template-columns: 1fr 1.5fr"]{grid-template-columns:1fr!important}div[style*="grid-template-columns: 1fr 1fr"]{grid-template-columns:1fr!important}}
        .spin { animation: spin 0.8s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default ContactPage;
